import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Layout";
import { formatINR, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Users, ClipboardList, IndianRupee, Inbox, TrendingUp, Star, AlertTriangle, Circle, Plane, CheckCircle2, XCircle } from "lucide-react";
import ManagerEodReport from "@/pages/ManagerEod";

function Kpi({ label, value, icon: Icon, testid, tone }) {
  return (
    <div data-testid={testid} className="bg-white border border-black/5 rounded-2xl p-6 card-lift">
      <div className="flex items-center justify-between">
        <div className="label-caps">{label}</div>
        <Icon size={16} className={tone || "text-neutral-500"} />
      </div>
      <div className="mt-3 font-display text-4xl leading-none text-royal-800">{value}</div>
    </div>
  );
}

function DeadlineBanner({ items }) {
  if (!items.length) return null;
  return (
    <div data-testid="deadline-banner" className="mx-8 mt-6 border-2 border-[#B02A00] bg-[#FFE7DE] text-[#7A1A00] rounded-2xl px-5 py-3 flex items-center gap-3">
      <AlertTriangle size={18} />
      <div className="flex-1 text-sm">
        <span className="font-semibold">{items.length}</span> work order{items.length > 1 ? "s" : ""} due within 48 hours — {items.slice(0, 3).map((w) => w.title).join(", ")}{items.length > 3 ? "…" : ""}
      </div>
      <Link to="/work-orders" className="text-xs underline underline-offset-4 font-semibold">Open</Link>
    </div>
  );
}

function ManagerStatusBar({ counts }) {
  const buckets = [
    { key: "Available Now", tone: "text-[#40916C]" },
    { key: "Online", tone: "text-[#B8974A]" },
    { key: "In Meeting", tone: "text-[#E5D7B4]" },
    { key: "Away for Few Hours", tone: "text-[#FF8A00]" },
    { key: "On Leave", tone: "text-[#B02A00]" },
    { key: "Not Available", tone: "text-neutral-400" },
  ];
  return (
    <div data-testid="manager-status-bar" className="grid grid-cols-2 md:grid-cols-6 gap-3">
      {buckets.map(({ key, tone }) => (
        <div key={key} className="bg-white border border-black/5 rounded-2xl p-4">
          <div className="label-caps flex items-center gap-2"><Circle size={8} className={`fill-current ${tone}`} /> {key}</div>
          <div className="font-display text-3xl mt-2 text-royal-800" data-testid={`mgr-count-${key.replace(/ /g, "-").toLowerCase()}`}>{counts[key] || 0}</div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [saExtras, setSaExtras] = useState(null);
  const [mgrExtras, setMgrExtras] = useState(null);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBusy, setLeaveBusy] = useState({});

  const loadLeaves = async () => {
    try { const { data } = await api.get("/leave-requests"); setLeaveRequests(data); } catch {}
  };

  const decideLeave = async (l, action) => {
    setLeaveBusy((b) => ({ ...b, [l.id]: true }));
    try {
      const { data } = await api.post(`/leave-requests/${l.id}/${action}`);
      if (action === "approve") {
        const s = data.summary || {};
        const r = (s.reassigned || []).length;
        const k = (s.skipped || []).length;
        toast.success(`Leave approved — ${r} project${r === 1 ? "" : "s"} reassigned${k ? `, ${k} skipped (no backup)` : ""}`);
      } else {
        toast.success("Leave rejected");
      }
      loadLeaves();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
    setLeaveBusy((b) => ({ ...b, [l.id]: false }));
  };

  useEffect(() => {
    const load = async () => {
      setAlerts((await api.get("/alerts/deadlines").catch(() => ({ data: [] }))).data);
      if (user.role === "SuperAdmin" || user.role === "TeamLeader" || user.role === "Manager") {
        const [d, wo] = await Promise.all([api.get("/dashboard/manager"), api.get("/work-orders")]);
        setData(d.data); setOrders(wo.data.slice(0, 6));
        setMgrExtras((await api.get("/dashboard/manager-extras").catch(() => ({ data: null }))).data);
        if (user.role === "SuperAdmin" || user.role === "TeamLeader") {
          setSaExtras((await api.get("/dashboard/super-admin-extras").catch(() => ({ data: null }))).data);
          loadLeaves();
        }
      } else {
        const [d, wo] = await Promise.all([api.get("/dashboard/freelancer"), api.get("/work-orders")]);
        setData(d.data); setOrders(wo.data);
      }
    };
    load();
  }, [user]);

  if (!data) return <div className="p-8 text-neutral-500 font-mono-tight text-sm">Loading…</div>;

  // Managers get the daily EOD report dashboard (strictly their own data).
  if (user.role === "Manager") {
    return (
      <div>
        <DeadlineBanner items={alerts} />
        <ManagerEodReport />
      </div>
    );
  }

  if (user.role === "SuperAdmin" || user.role === "TeamLeader") {
    return (
      <div>
        <PageHeader
          title={`Hi, ${user.name.split(" ")[0]}.`}
          description={user.role === "SuperAdmin" ? "Manager workload, task pipeline and org-wide KPIs." : "Your team, workload and today's tasks."}
          actions={
            <>
              {user.role === "SuperAdmin" && <Link to="/tasks" data-testid="sa-create-task-cta" className="btn-primary">+ Create Management Task</Link>}
              <Link to="/projects?new=1" className="btn-outline">+ New Project</Link>
            </>
          }
        />
        <DeadlineBanner items={alerts} />

        {/* Team + Work + Today — 'My Team' hidden for Managers per spec */}
        {mgrExtras && (
          <div className={`p-8 pt-6 grid grid-cols-1 gap-4 ${user.role === "Manager" ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
            {user.role !== "Manager" && (
              <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="my-team-card">
                <div className="label-caps">My Team</div>
                <div className="font-display text-4xl text-royal-800 mt-2">{mgrExtras.team.total} <span className="text-base text-ink/50">freelancers</span></div>
                <div className="mt-3 text-sm text-ink/70 flex gap-4">
                  <span><b>{mgrExtras.team.active}</b> Active</span>
                  <span><b>{mgrExtras.team.away}</b> Away</span>
                  <span><b>{mgrExtras.team.offline}</b> Offline</span>
                </div>
              </div>
            )}
            <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="my-work-card">
              <div className="label-caps">Work</div>
              <div className="font-display text-4xl text-royal-800 mt-2">{mgrExtras.work.active_projects} <span className="text-base text-ink/50">active projects</span></div>
              <div className="mt-3 text-sm text-ink/70 flex gap-4 flex-wrap">
                <span><b>{mgrExtras.work.due_today}</b> Due Today</span>
                <span className="text-[#B02A00]"><b>{mgrExtras.work.overdue}</b> Overdue</span>
                <span><b>{mgrExtras.work.awaiting_review}</b> Awaiting Review</span>
              </div>
            </div>
            <div className="bg-white border border-black/5 rounded-2xl p-6" data-testid="today-tasks-card">
              <div className="label-caps">Today's Tasks</div>
              <div className="font-display text-4xl text-royal-800 mt-2">{mgrExtras.tasks.assigned}</div>
              <div className="mt-3 text-sm text-ink/70 flex gap-4">
                <span><b>{mgrExtras.tasks.completed}</b> Completed</span>
                <span><b>{mgrExtras.tasks.pending}</b> Pending</span>
              </div>
            </div>
          </div>
        )}

        {/* Org KPIs */}
        <div className="px-8 pb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Kpi testid="kpi-active-freelancers" label="Active Freelancers" value={`${data.active_freelancers}/${data.total_freelancers}`} icon={Users} />
          <Kpi testid="kpi-active-projects" label="Active Projects" value={`${data.active_projects}/${data.total_projects}`} icon={ClipboardList} />
          <Kpi testid="kpi-pending-payments" label="Pending Payouts" value={formatINR(data.pending_payments_total)} icon={IndianRupee} tone="text-[#B02A00]" />
          <Kpi testid="kpi-open-orders" label="Open Work Orders" value={data.open_work_orders} icon={Inbox} tone="text-royal-700" />
        </div>

        {/* Manager status + productivity (SA + TeamLeader) */}
        {(user.role === "SuperAdmin" || user.role === "TeamLeader") && saExtras && (
          <div className="px-8 pb-6 space-y-4">
            <div>
              <div className="label-caps mb-2">Manager Overview</div>
              <ManagerStatusBar counts={saExtras.manager_status_counts} />
            </div>
            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-black/5 label-caps">Manager Productivity</div>
              <table className="fms-table w-full" data-testid="mgr-productivity">
                <thead><tr><th>Manager</th><th>Status</th><th>Tasks</th><th>Completion</th><th>On-Time</th><th>Overdue</th><th>Freelancers</th><th>Score</th></tr></thead>
                <tbody>
                  {saExtras.manager_summary.map((m) => (
                    <tr key={m.id} data-testid={`mgr-row-${m.id}`}>
                      <td>{m.name}</td>
                      <td className="text-xs font-mono-tight">{m.availability_status}</td>
                      <td className="font-mono-tight">{m.tasks_completed}/{m.tasks_total}</td>
                      <td className="font-mono-tight">{m.completion_pct}%</td>
                      <td className="font-mono-tight">{m.on_time_pct}%</td>
                      <td className="font-mono-tight text-[#B02A00]">{m.tasks_overdue}</td>
                      <td className="font-mono-tight">{m.freelancers_managed}</td>
                      <td><span className="px-2 py-0.5 rounded-full bg-royal-700 text-cream text-xs font-mono-tight">{m.score}/100</span></td>
                    </tr>
                  ))}
                  {saExtras.manager_summary.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-neutral-500">No managers yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Leave requests approval panel (SA + TeamLeader) */}
        {(user.role === "SuperAdmin" || user.role === "TeamLeader") && (
          <div className="px-8 pb-6">
            <div className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid="leave-approvals-panel">
              <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
                <div className="label-caps flex items-center gap-2 text-royal-800"><Plane size={12} /> Leave Requests</div>
                <span className="text-xs text-neutral-500 font-mono-tight">{leaveRequests.filter((l) => l.status === "Pending").length} pending</span>
              </div>
              <table className="fms-table w-full" data-testid="leave-requests-table">
                <thead><tr><th>Requester</th><th>Dates</th><th>Reason</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
                <tbody>
                  {leaveRequests.map((l) => (
                    <tr key={l.id} data-testid={`leave-req-${l.id}`}>
                      <td>{l.manager_name} <span className="text-xs text-neutral-500 font-mono-tight">· {l.manager_email}</span></td>
                      <td className="font-mono-tight text-xs">{l.start_date} → {l.end_date}</td>
                      <td className="text-sm text-ink/80">{l.reason}</td>
                      <td>
                        <span className={`px-2 py-0.5 text-xs font-mono-tight uppercase rounded ${l.status === "Approved" ? "bg-[#E9F5EE] text-[#175E33]" : l.status === "Rejected" ? "bg-[#FFE7DE] text-[#7A1A00]" : "bg-gold/20 text-royal-800"}`}>{l.status}</span>
                        {l.status === "Approved" && l.reassignment_summary && (
                          <div className="text-[10px] font-mono-tight text-neutral-500 mt-1">
                            {(l.reassignment_summary.reassigned || []).length} reassigned · {(l.reassignment_summary.skipped || []).length} skipped
                          </div>
                        )}
                      </td>
                      <td className="text-right whitespace-nowrap space-x-2">
                        {l.status === "Pending" ? (
                          <>
                            <button data-testid={`approve-leave-${l.id}`} disabled={leaveBusy[l.id]} onClick={() => decideLeave(l, "approve")} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-royal-700 text-cream disabled:opacity-50">
                              <CheckCircle2 size={12} /> Approve
                            </button>
                            <button data-testid={`reject-leave-${l.id}`} disabled={leaveBusy[l.id]} onClick={() => decideLeave(l, "reject")} className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-[#B02A00] text-[#B02A00] disabled:opacity-50">
                              <XCircle size={12} /> Reject
                            </button>
                          </>
                        ) : (
                          <span className="text-xs text-neutral-500 font-mono-tight">{l.decided_by_name || "—"}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {leaveRequests.length === 0 && <tr><td colSpan={5} className="text-center text-neutral-500 py-6">No leave requests yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent work orders */}
        <div className="px-8 pb-10">
          <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-black/5 flex items-center justify-between">
              <div className="label-caps">Recent Work Orders</div>
              <Link to="/work-orders" className="text-xs underline underline-offset-4">View all →</Link>
            </div>
            <table className="fms-table w-full" data-testid="dashboard-recent-orders">
              <thead><tr><th>Title</th><th>Status</th><th>Rate</th><th>Deadline</th></tr></thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td>{o.title}</td>
                    <td className="font-mono-tight text-xs">{o.status}</td>
                    <td className="font-mono-tight">{formatINR(o.rate)}</td>
                    <td className="font-mono-tight text-neutral-500">{o.deadline || "—"}</td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={4} className="text-center text-neutral-500 py-6">No work orders yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Freelancer
  return (
    <div>
      <PageHeader title={`Hi, ${user.name.split(" ")[0]}.`} description="Your active work, deadlines and earnings." />
      <DeadlineBanner items={alerts} />
      <div className="p-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi testid="kpi-active-orders" label="Active Orders" value={data.active_orders} icon={ClipboardList} />
        <Kpi testid="kpi-completed-orders" label="Completed" value={data.completed_orders} icon={TrendingUp} />
        <Kpi testid="kpi-pending-earnings" label="Pending Earnings" value={formatINR(data.pending_earnings)} icon={IndianRupee} tone="text-[#B02A00]" />
        <Kpi testid="kpi-total-earnings" label="Total Earned" value={formatINR(data.total_earnings)} icon={Star} />
      </div>
      <div className="px-8 pb-10">
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-black/5 label-caps">Your Work Orders</div>
          <table className="fms-table w-full">
            <thead><tr><th>Title</th><th>Status</th><th>Deadline</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id}><td>{o.title}</td><td className="font-mono-tight text-xs">{o.status}</td><td className="font-mono-tight text-neutral-500">{o.deadline || "—"}</td></tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={3} className="text-center text-neutral-500 py-6">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
